import type { Profile, DecodedToken } from '@/services/auth/types';
import { jwtDecode } from 'jwt-decode';
import { ref } from 'vue';
import axios from '@/axios';
import filters from '@/filters/filters';
import { $settings } from '@/provider/settings';
import { HTTP_STATUS } from '@/utils/httpStatusCodes';

// Helper to read from localStorage
const getFromStorage = <T>(
  key: string,
  defaultValue: T,
  parser?: (v: string) => T,
): T => {
  const value = localStorage.getItem(key);
  if (!value) return defaultValue;
  return parser ? parser(value) : (value as T);
};

// Reactive refs with localStorage backing
export const currentUserProfile = ref<Profile | null>(
  getFromStorage<Profile | null>('profile', null, (v) => JSON.parse(v) as Profile),
);

export const token = ref(getFromStorage('token', ''));

export const tokenExpiry = ref(getFromStorage('token-expiry', ''));

export const loggedInBasic = ref(
  getFromStorage('loggedInBasic', false, (v) => v === 'true'),
);

export const redirect = ref(getFromStorage('redirect', ''));

export const getProfileFromToken = (jwtToken: string): Profile | null => {
  if (!jwtToken) return null;
  const decodedToken = jwtDecode<DecodedToken>(jwtToken);
  const { oid, tid, name, preferred_username: preferredUsername } = decodedToken;

  const profile: Profile = {
    accountId: `${oid}.${tid}`,
    username: preferredUsername,
    fullName: name,
  };
  return profile;
};

export const getExpiryFromToken = (jwtToken: string): Date | null => {
  if (!jwtToken) return null;
  const decodedToken = jwtDecode<DecodedToken>(jwtToken);
  const { exp } = decodedToken;
  return exp ? new Date(exp * 1000) : null;
}

export const setProfile = (newProfile: Profile): void => {
  currentUserProfile.value = { ...currentUserProfile.value, ...newProfile };
  localStorage.setItem('profile', JSON.stringify(currentUserProfile.value));
};

export const setToken = (newToken: string | null): void => {
  if (!newToken) return;
  token.value = newToken;
  localStorage.setItem('token', newToken);
};

export const setTokenExpiry = (expiryDate: Date | null): void => {
  if (!expiryDate) return;
  const isoString = expiryDate.toISOString();
  tokenExpiry.value = isoString;
  localStorage.setItem('token-expiry', isoString);
};

export const getTokenExpiry = (): Date | null => {
  if (!tokenExpiry.value) return null;
  return new Date(tokenExpiry.value);
};

export const setLoggedInBasic = (value: boolean): void => {
  loggedInBasic.value = value;
  localStorage.setItem('loggedInBasic', String(value));
};

export const setRedirect = (value: string): void => {
  redirect.value = value;
  localStorage.setItem('redirect', value);
};

export const isExpired = (tokenDate?: Date | null): boolean => {
  if (!tokenDate) return true;
  const currentDate = new Date();
  return currentDate.getTime() > tokenDate.getTime();
};

// Teams-specific storage helpers (no reactive refs needed)
export const setTeamsToken = (
  accessToken: string,
  expiresIn: number,
  refreshToken: string,
): void => {
  const expiry = new Date();
  expiry.setSeconds(expiry.getSeconds() + expiresIn);
  localStorage.setItem('teams-token', accessToken);
  localStorage.setItem('teams-token-expiry', expiry.toISOString());
  localStorage.setItem('teams-refresh-token', refreshToken);
};

export const getTeamsToken = (): string | null =>
  localStorage.getItem('teams-token');

export const getTeamsTokenExpiry = (): Date | null => {
  const expiry = localStorage.getItem('teams-token-expiry');
  return expiry ? new Date(expiry) : null;
};

export const getTeamsRefreshToken = (): string | null =>
  localStorage.getItem('teams-refresh-token');

export const clearTeamsTokens = (): void => {
  localStorage.removeItem('teams-token');
  localStorage.removeItem('teams-token-expiry');
  localStorage.removeItem('teams-refresh-token');
};

export const clearAuth = (): void => {
  currentUserProfile.value = null;
  token.value = '';
  tokenExpiry.value = '';
  loggedInBasic.value = false;
  redirect.value = '';

  // Clear standard auth from localStorage
  localStorage.removeItem('profile');
  localStorage.removeItem('token');
  localStorage.removeItem('token-expiry');
  localStorage.removeItem('loggedInBasic');
  localStorage.removeItem('redirect');

  // Clear Teams auth tokens
  clearTeamsTokens();
};

export const loginBasic = async (
  username: string,
  password: string,
): Promise<void> => {
  const encodedData = `Basic ${window.btoa(`${username}:${password}`)}`;
  try {
    const response = await axios.post(
      [$settings.middlewareURI, 'auths', 'loginSAP'].join('/'),
      {},
      {
        headers: {
          'x-authorization': encodedData,
        },
      },
    );

    if (response.status < HTTP_STATUS.BAD_REQUEST) {
      setLoggedInBasic(true);
    }
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === HTTP_STATUS.UNAUTHORIZED
    ) {
      filters.showMessage('error', 'WX_GENERAL_LOGIN_FAILED_CHECK_CREDENTIALS');
    } else {
      console.error('Login error:', error);
    }
  }
};
